import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { COOKIES_CONFIG } from '@belong/common';
import { IconClose } from '@belong/icons';
import { ButtonBase, MediaGallery, Spinner } from '@belong/ui';
import {
  AddressModel,
  EmployeeAssignmentDetailModel,
  InspectionModel,
  MaintenanceMediaModel,
  MaintenanceModel,
  ReportDetailModel,
} from 'api/models';
import classNames from 'classnames/bind';
import clsx from 'clsx';
import Button from 'components/Button/Button';
import { BUTTON_TYPES } from 'components/Button/buttonTypes';
import Collapsible from 'components/Collapsible/Collapsible';
import Grid from 'components/Grid/Grid';
import { MaintenanceAccountResponsibilityBanner } from 'containercomponents/MaintenanceStatus/MaintenanceAccountResponsibility/MaintenanceAccountResponsibilityBanner';
import { MaintenanceCost } from 'containercomponents/MaintenanceStatus/MaintenanceCost/MaintenanceCost';
import { MaintenanceHomecareSpecialist } from 'containercomponents/MaintenanceStatus/MaintenanceHomecareSpecialist/MaintenanceHomecareSpecialist';
import styles from 'containercomponents/MaintenanceStatus/MaintenanceStatus.module.css';
import { MaintenanceStatusFooter } from 'containercomponents/MaintenanceStatus/MaintenanceStatusFooter/maintenance-status-footer';
import { MaintenanceBalance } from 'containercomponents/MaintenanceStatus/ModalMaintenanceBalance/ModalMaintenanceBalance';
import ModalMaintenanceHeader from 'containercomponents/MaintenanceStatus/ModalMaintenanceHeader/ModalMaintenanceHeader';
import { ModalMaintenanceSummary } from 'containercomponents/MaintenanceStatus/ModalMaintenanceSummary/ModalMaintenanceSummary';
import MaintenanceCancelModal from 'containercomponents/Modals/MaintenanceCancelModal/MaintenanceCancelModal';
import Space from 'corecomponents/Space/Space';
import { SPACE_TYPES } from 'corecomponents/Space/spaceTypes';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { useMaintenanceHasApprovedSubtask } from 'maintenance/hooks/useMaintenanceHasApprovedSubtask';
import { useMaintenanceHasPendingQuoteApproval } from 'maintenance/hooks/useMaintenanceHasPendingQuoteApproval';
import { useMaintenanceLabels } from 'maintenance/hooks/useMaintenanceLabels';
import { useRequestAccountResponsibility } from 'maintenance/hooks/useRequestAccountResponsibility';
import { RequestType } from 'models/enums';
import { parseCookies } from 'nookies';
import { BelongStandardImprovementLink } from 'pages/PostInspectionFlow/belong-standard-improvement-link/belong-standard-improvement-link';
import {
  fetchAccountUnitListings,
  fetchAccountProperties,
} from 'store/redux/homeowner-accounts/actions';
import {
  selectAccountPropertySummaries,
  selectAccountUnitListings,
} from 'store/redux/homeowner-accounts/selectors';
import {
  fetchHOTaskByTaskId,
  fetchRenterTaskByTaskId,
  maintenanceCurrentClear,
  fetchImprovementReportByTaskId,
  fetchEmployees,
  clearTaskById,
  fetchMaintenanceQuotes,
  fetchMaintenanceInvoices,
} from 'store/redux/maintenance/actions';
import { QuoteCentricSection } from 'maintenance/components/quote-centric-section/QuoteCentricSection';
import { QuoteCentricReceiptSection } from 'maintenance/components/quote-centric-section/QuoteCentricReceiptSection';
import { MaintenanceQuoteConsumerModel } from 'maintenance/types/quote-centric';
import { MaintenanceInvoiceConsumerModel } from 'maintenance/types/invoice';
import { selectCurrentTask } from 'store/redux/maintenance/selectors';
import { MAINTENACE_STATUS_MODAL_STRINGS } from 'strings/maintenance-status-modal.string';
import { pluralizeWithoutValue } from 'utils/pluralize';
import { checkIfCurrentStatusIsSame } from 'utils/validation';
import { GroupEmployeeAssignments } from './GroupEmployeeAssignments/GroupEmployeeAssignments';
import { GroupHomeownerReceiptCosts } from './GroupHomeownerReceiptCosts/GroupHomeownerReceiptCosts';
import { GroupImprovementCards } from './GroupImprovementCards/GroupImprovementCards';
import { GroupResidentReceiptCosts } from './GroupResidentReceiptCosts/GroupResidentReceiptCosts';
import { MaintenanceApprovalAndResponsibility } from './MaintenanceApprovalAndResponsibility/MaintenanceApprovalAndResponsibility';
import { MaintenanceCarousel } from './MaintenanceCarousel/MaintenanceCarousel';
import { maintenanceCostStrings } from './MaintenanceCost/maintenance-cost.constants';
import { useMaintenanceCostDisplayData } from './MaintenanceCost/use-maintenance-cost-display-data';
import { MaintenanceScheduleComponent } from './MaintenanceScheduleComponent/MaintenanceScheduleComponent';
import { Proposals } from './Proposals/Proposals';
import { SplitInfoBox } from './split-infobox/split-infobox';
import { WorkCompletionPrompts } from './WorkCompletionPrompts/work-completion-prompts';

const { JWT_TOKEN } = COOKIES_CONFIG;
const cx = classNames.bind(styles);

const isRenterValidTypes = {
  Repair: RequestType.Repair,
  Upgrade: RequestType.Upgrade,
  Addition: RequestType.Addition,
  KeysAndAccess: RequestType.KeysAndAccess,
  HaulingAndStorage: RequestType.HaulingAndStorage,
};

const isHomeownerValidTypes = {
  Repair: RequestType.Repair,
  Upgrade: RequestType.Upgrade,
  Addition: RequestType.Addition,
  HaulingAndStorage: RequestType.HaulingAndStorage,
  Preventative: RequestType.Preventative,
};

const editableRequestTypes = (
  requestType: MaintenanceModel['requestType'],
  status: MaintenanceModel['status'],
  isRenter: boolean,
  recurring: unknown,
  requestedBy: MaintenanceModel['requestedBy'],
) => {
  if (status === 'New') {
    if (isRenter && requestedBy === 'Resident') {
      if (isRenterValidTypes[requestType as keyof typeof isRenterValidTypes]) {
        return true;
      }
    }

    if (!isRenter && requestedBy === 'Homeowner') {
      if (
        isHomeownerValidTypes[requestType as keyof typeof isHomeownerValidTypes]
      ) {
        return true;
      }

      if (requestType === RequestType.Preventative && !!recurring) {
        return true;
      }
    }
  }

  return false;
};

const getFormattedAddress = (address: AddressModel) =>
  `${address.streetAddress}${address.unitNumber ? ` Unit ${address.unitNumber}` : ''}`;

const showEditButton = (isRenter: boolean, task: MaintenanceModel) => {
  if (!task) {
    return false;
  }

  return editableRequestTypes(
    task.requestType,
    task.status,
    isRenter,
    task.recurrence?.repeatFrequencyDays,
    task.requestedBy,
  );
};

type MaintenanceStatusProps = {
  taskId: string;
  inspection?: InspectionModel;
  onClosePress: () => void;
  history: RouteComponentProps['history'];
  isRenter?: boolean;
};

function MaintenanceStatus({
  taskId,
  inspection,
  onClosePress,
  history,
  isRenter = false,
}: MaintenanceStatusProps) {
  const [homecareSpecialist, setHomecareSpecialist] = useState<
    EmployeeAssignmentDetailModel | undefined
  >();
  const [isLoading, setLoading] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const propertiesData = useSelector(selectAccountPropertySummaries);
  const listings = useSelector(selectAccountUnitListings);
  const [improvementsReport, setImprovementReport] = useState<
    ReportDetailModel | undefined
  >();
  const [quotes, setQuotes] = useState<MaintenanceQuoteConsumerModel[]>([]);
  const [invoices, setInvoices] = useState<MaintenanceInvoiceConsumerModel[]>(
    [],
  );
  const accountResponsibilityRef = useRef<HTMLDivElement>(null);
  const { isEnabled: isBelongStandardsEnabled } =
    useFeatureFlag('belong-standards');

  const task = useSelector(selectCurrentTask) as unknown as MaintenanceModel;
  const { financePlanId, hasEligibleOpenBalance } =
    useMaintenanceCostDisplayData({
      task,
      userRole: isRenter ? 'Resident' : 'Homeowner',
    });
  const shouldRequestAccountResponsibility = useRequestAccountResponsibility(
    isRenter,
    task,
  );
  const hasPendingQuoteApproval = useMaintenanceHasPendingQuoteApproval(
    isRenter ? 'Resident' : 'Homeowner',
    task,
  );
  const hasApprovedSubtask = useMaintenanceHasApprovedSubtask(
    isRenter ? 'Resident' : 'Homeowner',
    task,
  );
  const isSpinnerVisible = !task?.uniqueId || isLoading;
  const maintenanceLabels = useMaintenanceLabels(isRenter, task);

  const dispatch = useDispatch();
  const taskIdentifier = task?.id;

  const fetchQuotes = useCallback(
    async (uniqueId: string) => {
      try {
        const result = await dispatch(fetchMaintenanceQuotes(uniqueId));
        setQuotes(result ?? []);
      } catch (e) {
        console.log(e);
      }
    },
    [dispatch],
  );

  const fetchInvoices = useCallback(
    async (uniqueId: string) => {
      try {
        const result = await dispatch(fetchMaintenanceInvoices(uniqueId));
        setInvoices(result ?? []);
      } catch (e) {
        console.log(e);
      }
    },
    [dispatch],
  );

  const fetchHomecareSpecialist = useCallback(async () => {
    try {
      if (taskIdentifier) {
        const _homecareSpecialist = await dispatch(
          fetchEmployees('Maintenance', taskIdentifier, 'HomecareSpecialist'),
        );
        setHomecareSpecialist(_homecareSpecialist);
      }
    } catch (e) {
      console.log(e);
      // The API client rejects with a body carrying `title`; `catch` binds
      // `unknown` under `strict`, so this restores the shape the check reads.
      if ((e as { title?: string })?.title === 'Not Found') {
        try {
          if (task?.home?.id) {
            const _homecareSpecialist = await dispatch(
              fetchEmployees('Home', task.home.id, 'HomecareSpecialist'),
            );
            setHomecareSpecialist(_homecareSpecialist);
          }
        } catch (err) {
          console.log(err);
        }
      }
    }
  }, [dispatch, taskIdentifier, task?.home?.id]);

  const fetchTask = useCallback(async () => {
    const fetchTaskAction = isRenter
      ? fetchRenterTaskByTaskId
      : fetchHOTaskByTaskId;
    try {
      setLoading(true);
      dispatch(clearTaskById());
      const currentTask = (await dispatch(
        fetchTaskAction(taskId),
      )) as unknown as MaintenanceModel;

      if (currentTask.requestType === RequestType.Group) {
        await dispatch(fetchAccountProperties());
        await dispatch(fetchAccountUnitListings());
        const reportsData = await dispatch(
          fetchImprovementReportByTaskId(taskId),
        );
        if (reportsData) setImprovementReport(reportsData);
      }

      if (currentTask.isQuoteCentric) {
        await Promise.all([
          fetchQuotes(currentTask.uniqueId),
          fetchInvoices(currentTask.uniqueId),
        ]);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }, [dispatch, fetchInvoices, fetchQuotes, isRenter, taskId]);

  useEffect(() => {
    fetchTask();

    return () => {
      dispatch(maintenanceCurrentClear());
    };
  }, []);

  useEffect(() => {
    if (taskIdentifier && !task?.isQuoteCentric) {
      fetchHomecareSpecialist();
    }
  }, [fetchHomecareSpecialist, taskIdentifier, task?.isQuoteCentric]);

  const handleShowCancelModal = () => {
    setCancelModal(true);
  };

  const handleCloseModal = () => {
    fetchTask();
    setCancelModal(false);
    setIsRecurring(false);
  };

  const handleShowCancelRecurringModal = () => {
    handleShowCancelModal();
    setIsRecurring(true);
  };

  const handleShowAddTaskModal = () => {
    if (!task) return;

    if (isRenter && task.requestType) {
      const cookies = parseCookies();
      const jwtToken = cookies[JWT_TOKEN.name];

      window.open(
        `${process.env.NEXT_PUBLIC_SMS_ASSIST_URL}${jwtToken}`,
        '_blank',
      );
    } else if (!isRenter && task.requestType) {
      history.push(
        `/homeowner/pro-services/${task.requestType}/${task.uniqueId}`,
      );
    }
  };

  const scheduleHomecareSpecialistCall = () => {
    if (homecareSpecialist?.employee?.calendarLink) {
      const formattedLink = homecareSpecialist.employee.calendarLink.includes(
        'https',
      )
        ? homecareSpecialist.employee.calendarLink
        : `https://${homecareSpecialist.employee.calendarLink}`;
      return window.open(formattedLink, '_blank');
    }
  };

  const renderBottom = () => {
    if (!task) {
      return;
    }

    // if pending consent return null.
    if (cancelModal) {
      return null;
    }

    if (
      task.status === 'New' &&
      checkIfCurrentStatusIsSame(task.requestedBy, isRenter) &&
      task.requestCategory !== 'InHomeRepairs'
    ) {
      const menuItems = [];
      if (task.recurrence?.uniqueId) {
        menuItems.push({
          name: (
            <Button
              className={cx('margin-mobile', 'w-full flex justify-start px-sm')}
              label="CANCEL THIS VISIT"
              buttonType={BUTTON_TYPES.TEXT_DANGER}
              onClick={handleShowCancelModal}
            />
          ),
          key: 'cancel-request',
        });

        if (!task.recurrence?.isCancelled) {
          menuItems.push({
            name: (
              <Button
                className={cx(
                  'margin-mobile',
                  'w-full flex justify-start px-sm',
                )}
                buttonType={BUTTON_TYPES.TEXT_DANGER}
                label="STOP RECURRING VISITS"
                onClick={handleShowCancelRecurringModal}
              />
            ),
            key: 'cancel-recurring',
          });
        }
      }

      if (showEditButton(isRenter, task)) {
        menuItems.push({
          name: (
            <Button
              className={cx('margin-mobile', 'w-full flex justify-start px-sm')}
              buttonType={BUTTON_TYPES.TEXT_DANGER}
              label="EDIT"
              onClick={handleShowAddTaskModal}
            />
          ),
          key: 'edit',
        });
      }

      menuItems.push({
        name: (
          <Button
            className={cx('margin-mobile', 'w-full flex justify-start px-sm')}
            label="CANCEL REQUEST"
            buttonType={BUTTON_TYPES.TEXT_DANGER}
            onClick={handleShowCancelModal}
          />
        ),
        key: 'cancel-visit',
      });

      return (
        <MaintenanceStatusFooter menuActions={menuItems} isRenter={isRenter} />
      );
    }

    return <MaintenanceStatusFooter isRenter={isRenter} />;
  };

  const renderForEveryTask = () => {
    if (!task.uniqueId) {
      return null;
    }

    const isGroupTask = task.requestType === RequestType.Group;
    // Every read below sits inside the `isGroupTask &&` branch, where this is
    // `task`; TypeScript cannot carry that narrowing across the separate const,
    // hence the assertions at those call sites.
    const groupTask = isGroupTask ? task : undefined;
    const hasAppointments = !!task.appointments.length;
    const hasPublishedInvoices =
      groupTask?.requestTypeDetails?.hasPublishedInvoices;
    const isAllMediaBefore = task.media?.every(
      (media) => media.stage === 'Before',
    );

    const isBelongStandardShown =
      isBelongStandardsEnabled && task?.belongStandardCategory !== undefined;

    return (
      <div className="relative overflow-x-hidden">
        <MaintenanceCancelModal
          isRenter={isRenter}
          show={cancelModal}
          isCancelRecurrence={isRecurring}
          close={handleCloseModal}
          taskId={task.uniqueId}
        />

        {!cancelModal && (
          <>
            <div className="mb-2xl">
              <ModalMaintenanceHeader
                title={task.summary}
                assessment={task.assessment}
                requestType={task.requestType}
                streetAddress={getFormattedAddress(task.address!)}
                jobId={task.id}
                propertyUniqueId={task.propertyUniqueId!}
                labels={maintenanceLabels}
                conversationId={task.agentConversationThreadId}
                unitId={task.homeUniqueId}
              />
            </div>
            {isGroupTask && (
              <>
                {((shouldRequestAccountResponsibility &&
                  task.requestCategory !== 'InHomeRepairs') ||
                  (hasPendingQuoteApproval &&
                    !hasApprovedSubtask &&
                    task.requestCategory === 'InHomeRepairs')) && (
                  <MaintenanceAccountResponsibilityBanner
                    componentRef={accountResponsibilityRef}
                  />
                )}

                <GroupEmployeeAssignments
                  task={task}
                  report={improvementsReport}
                  listingsData={listings}
                  propertiesData={propertiesData}
                  inspectionData={inspection}
                />
                {task.media && task.media.length > 0 && (
                  <div className="my-sm">
                    {/* @ts-expect-error -- Legacy typecheck baseline; see docs/development/typecheck-baseline-followups.md. Refs: ARCH-56 */}
                    <MediaGallery<MaintenanceMediaModel> media={task.media} />
                  </div>
                )}
                {task.requestCategory !== 'InHomeRepairs' && (
                  <div className="pb-lg pt-sm">
                    <GroupImprovementCards
                      report={improvementsReport}
                      groupTask={groupTask!}
                      hasAppointments={hasAppointments}
                      isRenter={isRenter}
                      listingsData={listings}
                    />
                  </div>
                )}
                {task.requestCategory === 'InHomeRepairs' &&
                  homecareSpecialist && (
                    <>
                      <MaintenanceHomecareSpecialist
                        homecareSpecialist={homecareSpecialist}
                        onScheduleACallPress={() =>
                          scheduleHomecareSpecialistCall()
                        }
                      />
                      <Space value={SPACE_TYPES.LG} />
                    </>
                  )}
                {hasAppointments && (
                  <>
                    <MaintenanceScheduleComponent
                      appointments={task.appointments}
                      maintenance={task}
                      showMaintenanceInfo={
                        task.requestCategory === 'InHomeRepairs'
                      }
                    />
                    <Space value={SPACE_TYPES.XL} />
                  </>
                )}

                {task.requestCategory !== 'InHomeRepairs' && isRenter && (
                  <GroupResidentReceiptCosts groupTask={groupTask!} />
                )}

                {task.requestCategory !== 'InHomeRepairs' && !isRenter && (
                  <GroupHomeownerReceiptCosts
                    groupTask={groupTask!}
                    report={improvementsReport}
                  />
                )}

                {hasPublishedInvoices && (
                  <MaintenanceBalance task={task} isRenter={isRenter} />
                )}

                {task.requestCategory === 'InHomeRepairs' && (
                  <div className="mt-lg">
                    <Proposals
                      maintenance={task}
                      userRole={isRenter ? 'Resident' : 'Homeowner'}
                      fetchTask={fetchTask}
                      accountResponsibilityRef={accountResponsibilityRef}
                      onScheduleHomecareSpecialistCall={() =>
                        scheduleHomecareSpecialistCall()
                      }
                    />
                  </div>
                )}
              </>
            )}

            {!isGroupTask && (
              <div className="relative">
                {task.requestCategory === 'InHomeRepairs' &&
                  !isAllMediaBefore && (
                    <MaintenanceCarousel
                      // `isAllMediaBefore` above is `undefined` when there is no
                      // media at all, so `!isAllMediaBefore` already let this
                      // branch run and dereference it. `url` is optional on the
                      // model but present on the image media this filters to.
                      images={task
                        .media!.filter(
                          (media) =>
                            media.mediaType === 'Image' && !media.subOwner,
                        )
                        .map((media) => ({
                          ...media,
                          url: media.url!,
                          mediaType: 'Image' as const,
                        }))}
                    />
                  )}

                {shouldRequestAccountResponsibility && (
                  <MaintenanceAccountResponsibilityBanner
                    componentRef={accountResponsibilityRef}
                  />
                )}

                <WorkCompletionPrompts
                  appointments={task.appointments}
                  maintenance={task}
                  isRenter={isRenter}
                />

                <ModalMaintenanceSummary
                  task={task}
                  hideDetails={isGroupTask}
                  collapsed={false}
                />

                {isBelongStandardShown && (
                  <div className="py-sm">
                    <BelongStandardImprovementLink
                      belongStandardCategory={task.belongStandardCategory!}
                      assessment={task.assessment}
                    />
                  </div>
                )}

                {homecareSpecialist && (
                  <div className="py-sm">
                    <MaintenanceHomecareSpecialist
                      homecareSpecialist={homecareSpecialist}
                      onScheduleACallPress={() =>
                        scheduleHomecareSpecialistCall()
                      }
                    />
                  </div>
                )}

                {hasAppointments && (
                  <div className="py-sm">
                    <MaintenanceScheduleComponent
                      appointments={task.appointments}
                      maintenance={task}
                      showMaintenanceInfo={false}
                    />
                  </div>
                )}
                {task.isQuoteCentric ? (
                  <>
                    <QuoteCentricSection
                      maintenanceUniqueId={task.uniqueId}
                      quotes={quotes}
                      onQuotesChange={() => fetchQuotes(task.uniqueId)}
                      sectionRef={accountResponsibilityRef}
                    />
                    <QuoteCentricReceiptSection invoices={invoices} />
                  </>
                ) : (
                  <>
                    {(task.actualCost || task.estimatedCost) && (
                      <div className="py-sm">
                        <Collapsible
                          title={
                            task.actualCost
                              ? maintenanceCostStrings.titleReceipt
                              : maintenanceCostStrings.titleEstimated
                          }
                          inner={
                            <div className="pt-sm">
                              <MaintenanceCost
                                task={task}
                                userRole={isRenter ? 'Resident' : 'Homeowner'}
                                onConsentChange={fetchTask}
                                accountResponsibilityRef={
                                  accountResponsibilityRef
                                }
                                showScheduleCall={
                                  !!homecareSpecialist?.employee?.calendarLink
                                }
                                onScheduleHomecareSpecialistCall={() =>
                                  scheduleHomecareSpecialistCall()
                                }
                                showItemPrices={
                                  task.requestCategory !== 'InHomeRepairs'
                                }
                                paddingHorizontal={false}
                                roundedTop
                              />
                              {!task.isLegacyInvoicing && !isRenter && (
                                <div className="mt-sm">
                                  <SplitInfoBox
                                    financePlanId={financePlanId}
                                    hasOpenBalance={hasEligibleOpenBalance}
                                    isMaintenanceCompleted={
                                      task.result === 'Completed'
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          }
                          collapsed={false}
                        />
                      </div>
                    )}
                    <Space value={SPACE_TYPES.XL} />
                    <MaintenanceApprovalAndResponsibility
                      task={task}
                      // The receiver maps over this unguarded, so an absent list already threw.
                      payments={task.payments!}
                      title={pluralizeWithoutValue(
                        task.payments?.length,
                        MAINTENACE_STATUS_MODAL_STRINGS[
                          'approvalAndResponsibility.title'
                        ],
                      )}
                      hideConsentStatus={
                        !task.estimatedCost && !task.actualCost
                      }
                    />
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex w-full flex-col items-center">
      <div className="flex w-full justify-end p-xs sm:px-sm sm:py-xs">
        <ButtonBase className="p-xs sm:p-sm" onClick={onClosePress}>
          <IconClose height={16} />
        </ButtonBase>
      </div>
      {isSpinnerVisible ? (
        <div className={clsx('m-auto w-screen', styles.spinner)}>
          <Spinner />
        </div>
      ) : (
        <div className={clsx('mb-3xl', styles.wrapper)}>
          <Grid>
            {task?.uniqueId && (
              <div
                className={cx('maintenanceStatusModal', {
                  hideUndefinedText: !task?.uniqueId,
                })}
              >
                {renderForEveryTask()}
                {task.requestCategory !== 'InHomeRepairs' && renderBottom()}
              </div>
            )}
          </Grid>
        </div>
      )}
    </div>
  );
}

export default MaintenanceStatus;
